import React, { useRef, useState, useMemo } from 'react';
import CalendarIcon from 'views/common/components/UI/Icons/CalendarIcon';
import StyledIconedBox, {
  StyledLocalizedDatePicker,
  StyledPopper,
  StyledDialog,
} from 'views/rebranded-module/common/components/Logical/MainWidgetV2/components/MainWidgetDatePickerV2/MainWidgetDatePickerV2.styles';

import { HighlightedPriceDay } from 'views/rebranded-module/common/components/Logical/MainWidgetV2/components/HighlightedPriceDay';
import { PriceLegendFooter } from 'views/rebranded-module/common/components/Logical/MainWidgetV2/components/PriceLegendFooter';

import Dinero from 'views/rebranded-module/common/utils/Dinero';
import moneyFormat from 'views/rebranded-module/common/utils/moneyFormat';

const MainWidgetDatePickerV2 = (props) => {
  const {
    error,
    helperText,
    className,
    id,
    onChange,
    disabled,
    resetOpenWidgetDateTimeField = () => {},
    showCalendarPrice = false,
    pricePerDay = [],
    ...otherProps
  } = props;
  const [open, setOpen] = useState(false);
  const inputRef = useRef();
  inputRef?.current?.blur();

  const minByCategory = useMemo(() => {
    const result = pricePerDay.reduce((acc, item) => {
      const { price_category: category, amount } = item;
      if (!category) return acc; // skip null or undefined

      const numericValue = amount.amount; // cents
      const isNewCategory = acc[category] === undefined;
      const isSmallerAmount = !isNewCategory && numericValue < acc[category].value;

      if (isNewCategory || isSmallerAmount) {
        acc[category] = {
          value: numericValue,
          display: moneyFormat(Dinero(amount)),
        };
      }

      return acc;
    }, {});

    return Object.fromEntries(
      Object.entries(result).map(([cat, { display }]) => [cat, display]),
    );
  }, [pricePerDay]);

  const slots = useMemo(() => {
    const slot = {};

    if (showCalendarPrice) {
      slot.day = (dayProps) => (
        <HighlightedPriceDay highlightDatesList={pricePerDay} {...dayProps} />
      );
    }

    if (showCalendarPrice && Object.keys(minByCategory).length > 0) {
      slot.actionBar = (dayProps) => (
        <PriceLegendFooter {...minByCategory} {...dayProps} />
      );
    }

    return slot;
  }, [showCalendarPrice, pricePerDay, minByCategory]);

  return (
    <StyledIconedBox
      error={error}
      className={className}
      icon={<CalendarIcon />}>
      <StyledLocalizedDatePicker
        open={open}
        onOpen={() => {
          if (!disabled) {
            setOpen(true);
          }
        }}
        format="MMM D, YYYY"
        onChange={(...args) => {
          if (document.getElementsByClassName('MuiPickersDay-root')?.length) {
            onChange(...args);
            setOpen(false);
            resetOpenWidgetDateTimeField();
          }
        }}
        slots={slots}
        slotProps={{
          textField: {
            fullWidth: true,
            variant: 'filled',
            helperText,
            InputLabelProps: { shrink: true },
            error,
            id,
            'data-testid': id,
            onClick: () => {
              if (!disabled) {
                setOpen(true);
              }
            },
          },
          nextIconButton: { 'data-testid': 'calendar-next-month' },
          previousIconButton: { 'data-testid': 'calendar-prev-month' },
          day: (ownerState) => ({
            'data-testid': `calendar-day-${ownerState.day.date()}`,
          }),
          popper: {
            component: StyledPopper,
            'data-testid': `calendar-container-${id}`,
            placement: 'auto-start',
            modifiers: [
              {
                name: 'flip',
                options: {
                  allowedAutoPlacements: ['top-start', 'bottom-start'],
                  fallbackPlacements: ['bottom-start', 'top-start'],
                },
              },
            ],
          },
          dialog: {
            'data-testid': `calendar-container-${id}`,
            sx: StyledDialog,
          },
        }}
        onClose={() => {
          setOpen(false);
          resetOpenWidgetDateTimeField();
        }}
        {...otherProps}
      />
    </StyledIconedBox>
  );
};

export { MainWidgetDatePickerV2 };
